export interface PerformanceMetric {
  id: string;
  action: string;
  target?: string;
  startTime: number;
  endTime: number;
  duration: number;
  customAttributes?: Record<string, any>;
}

const MAX_METRICS = 1000;

class PerformanceMetricsServiceImpl {
  private metrics: PerformanceMetric[] = [];

  async recordMetric(metric: PerformanceMetric): Promise<void> {
    this.metrics.push(metric);
    this.pruneOldMetrics();
  }

  async clearMetrics(): Promise<void> {
    this.metrics = [];
  }

  /**
   * Synchronous per-action rollup. Never allocates a filtered copy and returns immediately, so it can run inside a `pagehide` handler where the perf reporter must
   * build a beacon payload without awaiting. Keyed by `action`.
   */
  getActionSummarySync(): Record<
    string,
    { count: number; totalMs: number; maxMs: number; lastMs: number; lastAt: number }
  > {
    const summary: Record<string, { count: number; totalMs: number; maxMs: number; lastMs: number; lastAt: number }> =
      {};
    for (const metric of this.metrics) {
      const row = (summary[metric.action] ??= { count: 0, totalMs: 0, maxMs: 0, lastMs: 0, lastAt: 0 });
      row.count += 1;
      row.totalMs += metric.duration;
      if (metric.duration > row.maxMs) row.maxMs = metric.duration;
      if (metric.startTime >= row.lastAt) {
        row.lastAt = metric.startTime;
        row.lastMs = metric.duration;
      }
    }
    return summary;
  }

  private pruneOldMetrics(): void {
    if (this.metrics.length <= MAX_METRICS) return;

    // Keep the most recent MAX_METRICS entries
    this.metrics.sort((a, b) => b.startTime - a.startTime);
    this.metrics = this.metrics.slice(0, MAX_METRICS);
  }
}

export const PerformanceMetricsService = new PerformanceMetricsServiceImpl();
